import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { loginWithGoogleToken, requestLogin } from '@actions/user'
import { openModal } from '@actions/modal'
import LoginModal from '@components/LoginModal'
import { openGooglePopup } from '../../../utils/googlePopup'
import { safeRedirectTo } from '@shared/v2Redirect'
import { FIRST_ACCESS_ERROR, FIRST_ACCESS_SENT, checkFirstAccess } from '../firstAccess'

import V2Layout from '../V2Layout'
import logoDiretoBranco from '../assets/sm-direto-branco-cor.svg'
import logoSmBranco from '../assets/sm-educacao-horizontal-branco.png'

// Login do comprador para a área da conta V2 — mesmo padrão visual da etapa de
// identificação do fluxo de compra e MESMAS regras do V1 (MATRIZ):
// L-01/L-02 (obrigatórios e mensagens), L-03 (erro da API por campo), L-04
// (bloqueio por tentativas → LoginModal), L-06 (GA), L-07 (persitSession),
// L-21..L-23 (Google via popup). Sucesso → ?redirect_to= (validado) ou /v2/conta/pedidos.

const label = { display: 'block', margin: '18px 0 8px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6B6664' }
const input = { width: '100%', height: '52px', padding: '0 16px', border: '1.5px solid #E3DBD7', borderRadius: '12px', fontSize: '16px', color: '#373535', outline: 'none', boxSizing: 'border-box' }
const fieldError = { margin: '6px 0 0', fontSize: '13px', color: '#C2262B' }

export default function V2AccountLogin() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const authenticated = useSelector(state => state.sessionReducer && state.sessionReducer.authenticated)
  // pós-login o destino é a área logada (pedido de 02/09): primeiro o ?redirect_to=
  // da tela protegida que mandou para cá (pedido de 16/10, validado contra open
  // redirect); depois o returnTo interno da conta; por fim, os pedidos
  const wanted = (location.state && location.state.returnTo) || ''
  const returnTo =
    safeRedirectTo(searchParams.get('redirect_to')) ||
    (wanted.startsWith('/v2/conta/') && safeRedirectTo(wanted)) ||
    '/v2/conta/pedidos'
  // aviso vindo da redefinição de senha V2 (PasswordRecovery → state.notice)
  const notice = (location.state && location.state.notice) || ''

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [firstAccessNotice, setFirstAccessNotice] = useState('')

  const _handleLogin = () => {
    const next = {}
    if (!email) next.email = 'Por favor, preencha o seu email'
    if (!password) next.password = 'Por favor, preencha a sua senha'
    setErrors(next)
    if (Object.keys(next).length) return

    window.ga && window.ga('send', { hitType: 'event', eventCategory: 'conta', eventAction: 'click', eventLabel: 'logou_conta' })

    setSubmitting(true)
    setFirstAccessNotice('')
    // regra do V1 (LoginModal): conta sem senha recebe o e-mail de cadastro de senha
    checkFirstAccess(email).then(firstAccess => {
      if (!firstAccess) return _login()
      setSubmitting(false)
      if (firstAccess === 'sent') setFirstAccessNotice(FIRST_ACCESS_SENT(email))
      else setErrors({ email: FIRST_ACCESS_ERROR })
    })
  }

  const _login = () => {
    dispatch(requestLogin({ email, password, persitSession: true })).then(response => {
      setSubmitting(false)
      if (response && response.message) {
        setErrors({
          email: response.message.type === 'email' ? response.message.error : '',
          password: response.message.type === 'password' ? response.message.error : '',
        })
        if (response.message.type === 'blocked') {
          dispatch(openModal(<LoginModal active="login" blocked embedded />))
        }
      } else {
        navigate(returnTo)
      }
    })
  }

  const _handleGoogle = () => {
    openGooglePopup(
      (token, user) => {
        dispatch(loginWithGoogleToken(token, user)).then(() => navigate(returnTo))
      },
      err => console.error('Erro Google login:', err),
      returnTo
    )
  }

  // já logado → direto para o destino. Durante o login, não: a sessão fica
  // autenticada antes do setToken do axios, e o destino (ex.: detalhe do pedido
  // via redirect_to) buscaria com o token antigo — quem navega é o _login
  if (authenticated && !submitting) return <Navigate to={returnTo} replace />

  return (
    <V2Layout>
      <div className="v2-login">
      <div className="v2-form-wrap v2-login__form">
        <h1 style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '30px', lineHeight: '1.15', letterSpacing: '-0.6px', color: '#373535' }}>
          Entrar na minha conta
        </h1>
        <p style={{ margin: '10px 0 0', fontSize: '15px', lineHeight: '1.6', color: '#6B6664' }}>
          Acesse para acompanhar seus pedidos, baixar boletos e atualizar seus dados.
        </p>

        <div style={{ marginTop: '24px', border: '1px solid #EFE7E3', borderRadius: '18px', padding: '26px', background: '#fff' }}>
          {(firstAccessNotice || notice) && (
            <div id="v2-acclogin-notice" style={{ marginBottom: '6px', padding: '12px 14px', borderRadius: '12px', background: '#E8F4EE', color: '#16603C', fontSize: '14px', fontWeight: '700' }}>
              {firstAccessNotice || notice}
            </div>
          )}
          <label htmlFor="v2-acclogin-email" style={{ ...label, margin: '0 0 8px' }}>E-mail</label>
          <input
            id="v2-acclogin-email"
            className="v2d-input"
            type="email"
            placeholder="voce@email.com"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={input}
          />
          {errors.email && <p style={fieldError}>{errors.email}</p>}

          <label htmlFor="v2-acclogin-password" style={label}>Senha</label>
          <input
            id="v2-acclogin-password"
            className="v2d-input"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && _handleLogin()}
            style={input}
          />
          {errors.password && <p style={fieldError}>{errors.password}</p>}

          <p style={{ margin: '10px 0 0', fontSize: '13px' }}>
            <Link id="v2-acclogin-forgot" to="/v2/conta/esqueci-a-senha" style={{ color: '#D8262C', fontWeight: '700', textDecoration: 'none' }}>
              Esqueci a minha senha
            </Link>
          </p>

          <button
            id="v2-acclogin-submit"
            type="button"
            disabled={submitting}
            onClick={_handleLogin}
            style={{ marginTop: '20px', width: '100%', height: '52px', border: 'none', borderRadius: '12px', background: '#D8262C', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}
          >
            Entrar
          </button>

          <div style={{ margin: '20px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ flex: 1, height: '1px', background: '#EFE7E3' }} />
            <span style={{ fontSize: '12px', color: '#8E8783' }}>ou</span>
            <span style={{ flex: 1, height: '1px', background: '#EFE7E3' }} />
          </div>

          <button
            id="v2-acclogin-google"
            type="button"
            onClick={_handleGoogle}
            style={{ width: '100%', height: '50px', border: '1.5px solid #E3DBD7', borderRadius: '12px', background: '#fff', fontSize: '14px', fontWeight: '700', color: '#373535', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Entrar com Google
          </button>
          <div className="v2-login__foot">
            <span>Ainda não tem conta?</span>
            <button type="button" className="v2-linkbtn" id="v2-acclogin-buy" onClick={() => navigate('/')}>Comprar com voucher</button>
          </div>
        </div>
      </div>
        <aside className="v2-login__panel">
          <img src={logoDiretoBranco} alt="SM Direto" className="v2-login__logo" />
          <h2>Tudo sobre a compra dos materiais escolares em um lugar só.</h2>
          <ul>
            <li>Status de cada pedido</li>
            <li>Segunda via de boleto e nota fiscal</li>
            <li>Histórico de compras por aluno</li>
          </ul>
          <span className="v2-login__endorse">Um portal <img src={logoSmBranco} alt="SM Educação" /></span>
        </aside>
      </div>
    </V2Layout>
  )
}
